/**
 * Provides an administrative interface for managing user role assignments
 * across the application. Allows administrators to browse users by type,
 * search and review existing role assignments, assign or remove roles,
 * manage onboarding permissions, and persist role changes while supporting
 * pagination, role caching, and bulk role retrieval for improved performance.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";


import { getRoleNames } from "@/services/commonconfig";
import { ChevronDown } from "@untitledui/icons";
import { ApprovalType, Props, Role } from "../../types/access";
import { getMembersByUserType, getUserRoles, getUserRolesBulk, getUserTypes, saveUserRoles } from "../../services/accessService";

const ManageRoles: React.FC<Props> = ({ open, onClose, createdBy}) => {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [showRoles, setShowRoles] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [userTypes, setUserTypes] = useState<any[]>([]);
const [selectedUserType, setSelectedUserType] = useState("");
const [membersList, setMembersList] = useState<any[]>([]);
const [loadingMembers, setLoadingMembers] = useState(false);
const PAGE_SIZE = 100;

const roleCache = React.useRef<
  Record<string, string[]>
>({});

  const [userRolesMap, setUserRolesMap] = useState<Record<string, string[]>>({});

  const [roleMap, setRoleMap] = useState<Record<string, { role: Role; approval: ApprovalType }>>(
    {}
  );

  const [initialState, setInitialState] = useState("");
  const [saving, setSaving] = useState(false);
  

const filteredUsers = useMemo(() => {
  return membersList.filter((m) => {
    const name = (
      m.displayName || ""
    ).toLowerCase();

    const email = (
      m.mail ||
      m.userPrincipalName ||
      ""
    ).toLowerCase();

    return (
      name.includes(search.toLowerCase()) ||
      email.includes(search.toLowerCase())
    );
  });
}, [membersList, search]);


const [page, setPage] = useState(1);

const pagedUsers = useMemo(() => {
  return filteredUsers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
}, [filteredUsers, page]);

const [loadingRoles, setLoadingRoles] =
  useState(false);



  useEffect(() => {
  const loadUserTypes = async () => {
    try {
      const response = await getUserTypes();
      setUserTypes(response);
    } catch (error) {
      console.error(
        "Failed to load user types",
        error
      );
    }
  };

  loadUserTypes();
}, []);

useEffect(() => {
  const loadMembers = async () => {
  if (!selectedUserType) {
    setMembersList([]);
    return;
  }

  setLoadingMembers(true);

  setUserRolesMap({});
  roleCache.current = {};
  setPage(1);


  try {
    const members =
      await getMembersByUserType(
        Number(selectedUserType)
      );

    setMembersList(members);
  } catch (error) {
    console.error(
      "Failed to load members",
      error
    );

    setMembersList([]);
  } finally {
    setLoadingMembers(false);
  }
};

  loadMembers();
}, [selectedUserType]);


  useEffect(() => {
  const loadRoles = async () => {
    try {
      const res = await getRoleNames();

      const roleList: Role[] = res.map((r: any) => ({
        role_id: r.role_id,
        role_code: r.role_code,
        role_display_name: r.role_display_name,
      }));

      setRoles(roleList);
    } catch (error) {
      console.error("Failed to load roles", error);
      setRoles([]);
    }
  };

  loadRoles();
}, []);

useEffect(() => {
  if (!selectedUser) {
    setRoleMap({});
    setInitialState("");
    setShowRoles(false);
    return;
  }

  getUserRoles(selectedUser).then((res) => {
    const map: typeof roleMap = {};

    res.data.forEach((r: any) => {
      const role = roles.find(
        (x) => x.role_code === r.role_code
      );

      if (role) {
        map[role.role_code] = {
          role,
          approval: "approver",
        };
      }
    });

    setRoleMap(map);
    setInitialState(JSON.stringify(map));
    setShowRoles(true);
  });
}, [selectedUser, roles]);

  const handleAddUser = (user: any) => {
  setSelectedUser(
    user.mail ||
      user.userPrincipalName
  );

  setSelectedUserName(
    user.displayName ||
      user.mail ||
      user.userPrincipalName
  );
};

  const toggleRole = (role: Role) => {
    setRoleMap((prev) => {
      const next = { ...prev };
      if (next[role.role_code]) {
        delete next[role.role_code];
      } else {
        next[role.role_code] = {
          role,
          approval: "approver",
        };
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveUserRoles({
        user_principal_name: selectedUser,
        created_by : createdBy,
        roles: Object.values(roleMap).map((r) => ({
          role_code: r.role.role_code,
          approval_type: "approver",
        })),
      });
      setInitialState(JSON.stringify(roleMap));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setSelectedUser("");
    setSelectedUserName("");
    setRoleMap({});
    setShowRoles(false);
    onClose();
  };

  const hasChanges =
    selectedUser !== "" && initialState !== "" && JSON.stringify(roleMap) !== initialState;

  if (!open) return null;

  const UserCardSkeleton = () => (
  <div className="border border-gray-200 rounded-md p-4 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="flex-1">
        <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-60 bg-gray-200 rounded" />

        <div className="flex gap-2 mt-3">
          <div className="h-5 w-16 bg-gray-200 rounded" />
          <div className="h-5 w-20 bg-gray-200 rounded" />
        </div>
      </div>

      <div className="h-8 w-28 bg-gray-200 rounded" />
    </div>
  </div>
);

useEffect(() => {
  const loadPageRoles = async () => {
    if (!pagedUsers.length) {
      return;
    }

    setLoadingRoles(true);

    try {
    const emails = pagedUsers
  .map(
    (u) =>
      (
        u.mail ||
        u.userPrincipalName
      )?.toLowerCase()
  )
  .filter(
    (email) =>
      email &&
      !roleCache.current[email]
  );
if (!emails.length) {
  setLoadingRoles(false);
  return;
}
      const res =
        await getUserRolesBulk(emails);

      setUserRolesMap((prev) => ({
        ...prev,
        ...res.data,
      }));

      roleCache.current = {
        ...roleCache.current,
        ...res.data,
      };
    } catch (err) {
      console.error(
        "Failed loading roles",
        err
      );
    } finally {
      setLoadingRoles(false);
    }
  };

  loadPageRoles();
}, [pagedUsers]);


  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">Daraja Onboarding</h2>
            </div>

            <div className="flex items-center gap-3">
              {showRoles && (
                <>
                  {hasChanges && (
                    <div className="text-orange-600 border border-orange-300 bg-orange-50 px-3 py-1 rounded-md text-sm">
                      Unsaved
                    </div>
                  )}

                  <button
                    className="bg-[#e7e5e0] text-gray-700 px-4 py-1.5 rounded-md text-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    className="bg-[var(--color-brand)]  text-white px-4 py-1.5 rounded-md text-sm"
                    onClick={handleSave}
                    disabled={!selectedUser || saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="p-5">
            {!showRoles && (
              <>
             <div className="flex gap-4 mb-4">
          <div className="w-1/3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Type
            </label>

  <div className="relative">
    <select
      value={selectedUserType}
      onChange={(e) => {
        setSelectedUserType(e.target.value);
        setSearch("");
      }}
      className="w-full border border-gray-200 rounded-md p-2 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-orange-400"
    >
      <option value="">Select User Type</option>

      {userTypes.map((type) => (
        <option key={type.id} value={type.id}>
          {type.user_type}
        </option>
      ))}
    </select>

    <ChevronDown
      className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none"
    />
  </div>
</div>

  <div className="flex-1">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Search User
    </label>

    <input
      type="text"
      disabled={!selectedUserType}
      placeholder="Search users by name or email..."
      className="w-full border border-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  </div>
</div>

               <div className="space-y-3 max-h-[400px] overflow-auto">
  {loadingMembers ? (
    <>
      <UserCardSkeleton />
      <UserCardSkeleton />
      <UserCardSkeleton />
      <UserCardSkeleton />
      <UserCardSkeleton />
    </>
  ) : (
   pagedUsers.map((u) => {
  const email = (
    u.mail ||
    u.userPrincipalName
  )?.toLowerCase();

  const assignedRoles =
    userRolesMap[email] || [];

  return (
    <div
      key={u.id}
      className="border border-gray-200 rounded-md p-4 flex justify-between items-center"
    >
      <div>
        <div className="text-sm font-medium text-gray-800">
          {u.displayName || "User"}
        </div>

        <div className="text-xs text-gray-500">
          {u.mail || u.userPrincipalName}
        </div>

        <div className="flex flex-wrap gap-1 mt-1">
          {loadingRoles ||
 userRolesMap[email] === undefined ? (
            <>
              <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
            </>
          ) : assignedRoles.length > 0 ? (
  assignedRoles.map((code) => (
    <span
      key={code}
      className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded"
    >
      {ROLE_NAME_MAP[code] || code}
    </span>
  ))
) : (
  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">
    No Roles Assigned
  </span>
)}
        </div>
      </div>

      <button
        className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm"
        onClick={() => handleAddUser(u)}
      >
        Assign Roles
      </button>
    </div>
  );
})

  )}
</div>
              </>
            )}

            {showRoles && (
              <div className="border border-gray-200 rounded-md p-4">
                <div className="text-sm font-medium text-gray-800">{selectedUserName}</div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {roles.map((role) => {
                    const selected = !!roleMap[role.role_code];

                    return (
                      <div
                        key={role.role_code}
                        onClick={() => toggleRole(role)}
                        className={`px-3 py-1.5 text-sm rounded-md cursor-pointer border ${
                          selected
                            ? "bg-[#fff4ec] border-orange-300 text-orange-600"
                            : "bg-white border-gray-200 text-gray-700"
                        }`}
                      >
                        {ROLE_NAME_MAP[role.role_code] || role.role_code}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
     <div className="flex items-center justify-center gap-4 mt-4">
  <button
    className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm  disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
    disabled={page === 1}
    onClick={() =>
      setPage((prev) => prev - 1)
    }
  >
    Previous
  </button>

  <span className="text-sm font-medium text-gray-700">
    Page {page} of{" "}
    {Math.ceil(
      filteredUsers.length / PAGE_SIZE
    )}
  </span>

  <button
    className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm  disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
    disabled={
      page >=
      Math.ceil(
        filteredUsers.length / PAGE_SIZE
      )
    }
    onClick={() =>
      setPage((prev) => prev + 1)
    }
  >
    Next
  </button>
</div>
      </div>
    </div>
  );
};

export default ManageRoles;
